import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contratsApi } from '../services/api';
import { Contrat, CONTRACT_TYPES, PAYMENT_MODES } from '../types';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import './Pages.css';

const ITEMS_PER_PAGE = 10;
// Doit rester aligne sur CAutoBlockSentSmsCount (backend Routes.Contrats.pas) :
// au Nieme SMS parti, un contrat expire bascule en « Bloqué ».
const AUTO_BLOCK_SMS_COUNT = 3;
const SHORT_TERM_CONTRACT_TYPES = new Set([
  'Instance de confirmation',
  'Instance de paiement',
]);
const SHORT_TERM_PAYMENT_MODES = new Set([
  'Instance',
]);

interface SmsHistoryItem {
  id: number;
  phone_number: string;
  message_text: string;
  status: string;
  created_at: string;
  sent_at?: string | null;
  error_message?: string | null;
}

interface SmsHistoryState {
  loading: boolean;
  smsCount: number;
  sentCount: number;
  items: SmsHistoryItem[];
  error?: string;
}

interface RenewalDialogState {
  open: boolean;
  contrat: Contrat | null;
  modeReglt: string;
}

const SmsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M4 5.5C4 4.67 4.67 4 5.5 4h13C19.33 4 20 4.67 20 5.5v9c0 .83-.67 1.5-1.5 1.5h-4.13L10 20v-4H5.5C4.67 16 4 15.33 4 14.5v-9Z"
      fill="currentColor"
    />
    <path d="M7 8h10v1.5H7V8Zm0 3h7v1.5H7V11Z" fill="#ffffff" />
  </svg>
);

const HistoryIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9.3 3.75h5.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M12 4.2v1.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <circle cx="12" cy="12.8" r="6.9" fill="#ffffff" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="12" cy="12.8" r="1.1" fill="#d84b57" />
    <path d="M12 12.8V9.9" stroke="#d84b57" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M12 12.8l2.15 1.55" stroke="#d84b57" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="m7.05 7.05 9.9 9.9m0-9.9-9.9 9.9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M4 17.25V20h2.75L17.8 8.94l-2.75-2.75L4 17.25Zm14.71-9.04a1 1 0 0 0 0-1.42l-1.5-1.5a1 1 0 0 0-1.42 0l-1.17 1.17 2.75 2.75 1.34-1.34Z"
      fill="currentColor"
    />
  </svg>
);

const RenewIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 4a8 8 0 1 1-7.75 10h1.58A6.5 6.5 0 1 0 12 5.5c-1.8 0-3.43.73-4.6 1.9L10 10H4V4l2.34 2.34A7.96 7.96 0 0 1 12 4Z"
      fill="currentColor"
    />
    <path d="M12 8.25a.75.75 0 0 1 .75.75v2.56l1.98 1.15a.75.75 0 0 1-.76 1.3l-2.34-1.36a.75.75 0 0 1-.38-.65V9a.75.75 0 0 1 .75-.75Z" fill="currentColor" />
  </svg>
);

const normalizeContractType = (contractType: string): string =>
  contractType === 'Expire' ? 'Bloque' : contractType;

const toDateInputValue = (value: string): string => value.split('T')[0];

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeSearchValue = (value?: string | null): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizePhoneValue = (value?: string | null): string =>
  (value || '').replace(/\D/g, '');

const calculateContractEndDate = (
  startDate: string,
  contractType: string,
  paymentMode: string
): string => {
  const [year, month, day] = startDate.split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }

  const endDate = new Date(year, month - 1, day);
  if (
    SHORT_TERM_CONTRACT_TYPES.has(normalizeContractType(contractType)) ||
    SHORT_TERM_PAYMENT_MODES.has(paymentMode)
  ) {
    endDate.setDate(endDate.getDate() + 30);
  } else {
    endDate.setFullYear(endDate.getFullYear() + 1);
  }

  return formatDateInput(endDate);
};

const ExpiredContrats: React.FC = () => {
  const navigate = useNavigate();
  const [contrats, setContrats] = useState<Contrat[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(10);
  const [searchDenomination, setSearchDenomination] = useState('');
  const [searchTelephone, setSearchTelephone] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sendingContractId, setSendingContractId] = useState<number | null>(null);
  const [renewingContractId, setRenewingContractId] = useState<number | null>(null);
  const [historyContrat, setHistoryContrat] = useState<Contrat | null>(null);
  const [renewalDialog, setRenewalDialog] = useState<RenewalDialogState>({
    open: false,
    contrat: null,
    modeReglt: '',
  });
  const [smsHistoryByContract, setSmsHistoryByContract] = useState<Record<number, SmsHistoryState>>({});
  const { showToast, showConfirm } = useToast();
  const { hasPermission } = useAuth();

  const loadExpiredContrats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contratsApi.getExpired(days);
      setContrats(response.data);
      setSmsHistoryByContract({});
    } catch (err) {
      showToast('Erreur lors du chargement des contrats', 'error');
    } finally {
      setLoading(false);
    }
  }, [days, showToast]);

  useEffect(() => {
    void loadExpiredContrats();
    setCurrentPage(1);
  }, [loadExpiredContrats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterMode, searchDenomination, searchTelephone]);

  const loadSmsHistory = useCallback(async (
    contratId: number,
    force = false,
    notifyOnError = false
  ): Promise<SmsHistoryState | null> => {
    const cached = smsHistoryByContract[contratId];
    if (!force && cached && (cached.loading || !cached.error)) {
      return cached;
    }

    setSmsHistoryByContract((prev) => ({
      ...prev,
      [contratId]: {
        loading: true,
        smsCount: prev[contratId]?.smsCount ?? 0,
        sentCount: prev[contratId]?.sentCount ?? 0,
        items: prev[contratId]?.items ?? [],
      },
    }));

    try {
      const response = await contratsApi.getSmsHistory(contratId);
      const nextState: SmsHistoryState = {
        loading: false,
        smsCount: Number(response.data?.sms_count ?? 0),
        sentCount: Number(response.data?.sent_count ?? 0),
        items: Array.isArray(response.data?.items) ? response.data.items : [],
      };

      setSmsHistoryByContract((prev) => ({
        ...prev,
        [contratId]: nextState,
      }));

      return nextState;
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Erreur lors du chargement de l historique SMS';

      setSmsHistoryByContract((prev) => ({
        ...prev,
        [contratId]: {
          loading: false,
          smsCount: prev[contratId]?.smsCount ?? 0,
          sentCount: prev[contratId]?.sentCount ?? 0,
          items: prev[contratId]?.items ?? [],
          error: message,
        },
      }));

      if (notifyOnError) {
        showToast(message, 'error');
      }

      return null;
    }
  }, [showToast, smsHistoryByContract]);

  const normalizedDenominationSearch = normalizeSearchValue(searchDenomination);
  const normalizedTelephoneSearch = normalizePhoneValue(searchTelephone);
  const hasActiveFilters =
    Boolean(filterType) ||
    Boolean(filterMode) ||
    Boolean(normalizedDenominationSearch) ||
    Boolean(normalizedTelephoneSearch);

  const filteredContrats = contrats.filter((contrat) => {
    const matchesType = !filterType || contrat.t_contrat === filterType;
    const matchesMode = !filterMode || contrat.mode_reglt === filterMode;
    const matchesDenomination =
      !normalizedDenominationSearch ||
      normalizeSearchValue(contrat.client_name).includes(normalizedDenominationSearch);
    const matchesTelephone =
      !normalizedTelephoneSearch ||
      normalizePhoneValue(contrat.telephone).includes(normalizedTelephoneSearch);

    return matchesType && matchesMode && matchesDenomination && matchesTelephone;
  });

  const totalPages = Math.ceil(filteredContrats.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedContrats = filteredContrats.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    paginatedContrats.forEach((contrat) => {
      if (!smsHistoryByContract[contrat.id]) {
        void loadSmsHistory(contrat.id);
      }
    });
  }, [loadSmsHistory, paginatedContrats, smsHistoryByContract]);

  const getDaysRemaining = (dateFin: string) => {
    const endDate = new Date(dateFin);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleString();
  };

  const formatHistoryStatus = (status: string) => {
    switch (status) {
      case 'queued':
        return 'En attente';
      case 'processing':
        return 'Traitement';
      case 'sent':
        return 'Envoye';
      case 'failed':
        return 'Echec';
      case 'cancelled':
        return 'Annule';
      default:
        return status;
    }
  };

  const getHistoryStatusClass = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'failed';
      case 'processing':
        return 'processing';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'queued';
    }
  };

  const expiredCount = filteredContrats.filter((contrat) => getDaysRemaining(contrat.date_fin) < 0).length;
  const expiringCount = filteredContrats.filter((contrat) => getDaysRemaining(contrat.date_fin) >= 0).length;

  const sendSmsForContrat = useCallback((contrat: Contrat) => {
    if (!contrat.telephone?.trim()) {
      showToast('Aucun numero de telephone disponible pour ce client', 'warning');
      return;
    }

    const executeSend = async () => {
      try {
        setSendingContractId(contrat.id);
        const response = await contratsApi.sendSms(contrat.id);
        showToast(response.data?.message || 'SMS envoye avec succes', 'success');

        if (response.data?.contract_blocked) {
          showToast(
            `${contrat.client_name} : contrat passe en "Bloque" apres ${AUTO_BLOCK_SMS_COUNT} SMS`,
            'warning'
          );
          await loadExpiredContrats();
        } else {
          if (response.data?.block_failed) {
            showToast(
              `${contrat.client_name} : le passage en "Bloque" a echoue (SMS bien envoye) - verifier les logs serveur`,
              'error'
            );
          }
          await loadSmsHistory(contrat.id, true);
        }
      } catch (err: any) {
        showToast(
          err?.response?.data?.error || err?.response?.data?.message || 'Erreur lors de l envoi du SMS',
          'error'
        );
      } finally {
        setSendingContractId(null);
      }
    };

    showConfirm(
      `Envoyer un SMS a ${contrat.client_name} (${contrat.telephone}) ?`,
      () => { void executeSend(); },
      'Envoyer SMS'
    );
  }, [loadExpiredContrats, loadSmsHistory, showConfirm, showToast]);

  const openSmsHistory = useCallback((contrat: Contrat) => {
    setHistoryContrat(contrat);
    void loadSmsHistory(contrat.id, false, true);
  }, [loadSmsHistory]);

  const editContrat = useCallback((contrat: Contrat) => {
    navigate(`/?edit=${contrat.id}&return=expired`);
  }, [navigate]);

  const openRenewalDialog = useCallback((contrat: Contrat) => {
    setRenewalDialog({
      open: true,
      contrat,
      modeReglt: contrat.mode_reglt || PAYMENT_MODES[0],
    });
  }, []);

  const closeRenewalDialog = useCallback(() => {
    if (renewingContractId !== null) {
      return;
    }

    setRenewalDialog({
      open: false,
      contrat: null,
      modeReglt: '',
    });
  }, [renewingContractId]);

  const confirmRenewal = useCallback(async () => {
    if (!renewalDialog.contrat) {
      return;
    }

    const contrat = renewalDialog.contrat;
    const newStartDate = toDateInputValue(contrat.date_fin);
    const newEndDate = calculateContractEndDate(newStartDate, contrat.t_contrat, renewalDialog.modeReglt);

    try {
      setRenewingContractId(contrat.id);
      const response = await contratsApi.update(contrat.id, {
        id_client: contrat.id_client,
        date_debut: newStartDate,
        date_fin: newEndDate,
        t_contrat: contrat.t_contrat,
        mode_reglt: renewalDialog.modeReglt,
        reset_sms_counter: true,
      });
      // Client deja envoye vers Remote dont le contrat n'a pas pu y etre mis a jour
      const remoteWarning = (response.data?.remote_warning as string | undefined);
      showToast(remoteWarning || 'Contrat renouvele avec succes', remoteWarning ? 'warning' : 'success');
      if (historyContrat?.id === contrat.id) {
        setHistoryContrat(null);
      }
      setRenewalDialog({
        open: false,
        contrat: null,
        modeReglt: '',
      });
      await loadExpiredContrats();
    } catch (err) {
      showToast('Erreur lors du renouvellement du contrat', 'error');
    } finally {
      setRenewingContractId(null);
    }
  }, [historyContrat?.id, loadExpiredContrats, renewalDialog, showToast]);

  const historyData = historyContrat ? smsHistoryByContract[historyContrat.id] : null;
  const renewalStartDate = renewalDialog.contrat ? toDateInputValue(renewalDialog.contrat.date_fin) : '';
  const renewalEndDate = renewalDialog.contrat
    ? calculateContractEndDate(renewalStartDate, renewalDialog.contrat.t_contrat, renewalDialog.modeReglt)
    : '';

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Contrats expires et expirant bientot</h1>
      </div>

      <div className="filters">
        <div className="days-filter">
          <label>Inclure les contrats expirant dans les</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={5}>5 jours</option>
            <option value={10}>10 jours</option>
            <option value={15}>15 jours</option>
            <option value={30}>30 jours</option>
            <option value={60}>60 jours</option>
            <option value={90}>90 jours</option>
          </select>
        </div>
        <input
          type="text"
          placeholder="Rechercher par denomination..."
          value={searchDenomination}
          onChange={(e) => setSearchDenomination(e.target.value)}
          className="search-input"
        />
        <input
          type="tel"
          placeholder="Rechercher par telephone..."
          value={searchTelephone}
          onChange={(e) => setSearchTelephone(e.target.value)}
          className="search-input"
        />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">Tous les types</option>
          {CONTRACT_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
          <option value="">Tous les modes</option>
          {PAYMENT_MODES.map((mode) => (
            <option key={mode} value={mode}>{mode}</option>
          ))}
        </select>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value expired-value">{expiredCount}</span>
          <span className="stat-label">expires</span>
        </div>
        <div className="stat-item">
          <span className="stat-value expiring-value">{expiringCount}</span>
          <span className="stat-label">expirant dans les {days} prochains jours</span>
        </div>
        <div className="stat-item">
          <span className="stat-value total-value">{filteredContrats.length}</span>
          <span className="stat-label">total</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : filteredContrats.length === 0 ? (
        <div className="empty-state">
          <p>
            {hasActiveFilters
              ? 'Aucun contrat ne correspond aux filtres saisis.'
              : `Aucun contrat expire ou expirant dans les ${days} prochains jours.`}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Telephone</th>
                <th>Type de contrat</th>
                <th>Mode reglement</th>
                <th>Date fin</th>
                <th>Statut</th>
                <th>SMS</th>
              </tr>
            </thead>
            <tbody>
              {paginatedContrats.map((contrat) => {
                const daysRemaining = getDaysRemaining(contrat.date_fin);
                const isExpired = daysRemaining < 0;
                const absDays = Math.abs(daysRemaining);
                const smsHistory = smsHistoryByContract[contrat.id];
                const smsCountLabel = smsHistory?.loading && smsHistory.smsCount === 0
                  ? '...'
                  : String(smsHistory?.smsCount ?? 0);
                const sentSmsCount = smsHistory?.sentCount ?? 0;
                const smsBeforeBlock = Math.max(0, AUTO_BLOCK_SMS_COUNT - sentSmsCount);
                const isBlocked = contrat.t_contrat === 'Bloqué';
                const sendSmsTitle = !contrat.telephone?.trim()
                  ? 'Numero de telephone indisponible'
                  : isExpired && !isBlocked
                    ? `Envoyer un SMS (${sentSmsCount} envoye(s) - ${smsBeforeBlock} avant blocage)`
                    : `Envoyer un SMS (${smsHistory?.smsCount ?? 0} deja envoye(s))`;

                return (
                  <tr
                    key={contrat.id}
                    className={isExpired ? 'row-critical' : daysRemaining <= 3 ? 'row-warning-critical' : daysRemaining <= 7 ? 'row-warning' : ''}
                  >
                    <td>{contrat.client_name}</td>
                    <td>{contrat.telephone || '-'}</td>
                    <td>{contrat.t_contrat}</td>
                    <td>{contrat.mode_reglt}</td>
                    <td>{new Date(contrat.date_fin).toLocaleDateString()}</td>
                    <td>
                      {isExpired ? (
                        <span className="days-badge critical">
                          Expire depuis {absDays} jour{absDays > 1 ? 's' : ''}
                        </span>
                      ) : daysRemaining === 0 ? (
                        <span className="days-badge critical">
                          Expire aujourd hui
                        </span>
                      ) : (
                        <span className={`days-badge ${daysRemaining <= 3 ? 'warning-critical' : daysRemaining <= 7 ? 'warning' : 'normal'}`}>
                          {daysRemaining} jour{daysRemaining > 1 ? 's' : ''} restant{daysRemaining > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="sms-actions">
                        <button
                          type="button"
                          className="sms-icon-btn sms-send-btn"
                          onClick={() => sendSmsForContrat(contrat)}
                          disabled={!contrat.telephone?.trim() || sendingContractId === contrat.id}
                          title={sendSmsTitle}
                        >
                          <span className="action-icon">
                            <SmsIcon />
                          </span>
                          <span className="sms-count-badge">{sendingContractId === contrat.id ? '...' : smsCountLabel}</span>
                        </button>
                        <button
                          type="button"
                          className="sms-icon-btn sms-history-btn"
                          onClick={() => openSmsHistory(contrat)}
                          title="Voir l historique des SMS"
                        >
                          <span className="action-icon">
                            <HistoryIcon />
                          </span>
                        </button>
                        {hasPermission('contrats', 'edit') && (
                          <>
                            <button
                              type="button"
                              className="sms-icon-btn contract-action-btn edit-action-btn"
                              onClick={() => editContrat(contrat)}
                              title="Modifier le contrat"
                            >
                              <span className="action-icon">
                                <EditIcon />
                              </span>
                            </button>
                            <button
                              type="button"
                              className="sms-icon-btn contract-action-btn renew-action-btn"
                              onClick={() => openRenewalDialog(contrat)}
                              disabled={renewingContractId === contrat.id}
                              title="Renouveler le contrat"
                            >
                              <span className="action-icon">
                                <RenewIcon />
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filteredContrats.length > ITEMS_PER_PAGE && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            &laquo;&laquo;
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            &laquo;
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            &raquo;
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            &raquo;&raquo;
          </button>

          <span className="pagination-info">
            {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredContrats.length)} sur {filteredContrats.length}
          </span>
        </div>
      )}

      {historyContrat && (
        <div className="sms-history-overlay" onClick={() => setHistoryContrat(null)}>
          <div className="sms-history-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sms-history-header">
              <div>
                <h2>Historique SMS</h2>
                <p>
                  {historyContrat.client_name}
                  {' - '}
                  {historyData?.smsCount ?? 0} SMS
                  {' / '}
                  {historyData?.sentCount ?? 0} envoyes
                </p>
              </div>
              <button
                type="button"
                className="sms-history-close"
                onClick={() => setHistoryContrat(null)}
                title="Fermer"
              >
                <CloseIcon />
              </button>
            </div>

            {!historyData || historyData.loading ? (
              <div className="loading">Chargement de l historique...</div>
            ) : historyData.items.length === 0 ? (
              <div className="empty-state compact-empty-state">
                <p>Aucun SMS enregistre pour ce contrat.</p>
              </div>
            ) : (
              <div className="sms-history-list">
                {historyData.items.map((item) => (
                  <div key={item.id} className="sms-history-item">
                    <div className="sms-history-item-top">
                      <span className={`history-status-badge ${getHistoryStatusClass(item.status)}`}>
                        {formatHistoryStatus(item.status)}
                      </span>
                      <span className="sms-history-date">{formatDateTime(item.sent_at || item.created_at)}</span>
                    </div>
                    <div className="sms-history-meta">
                      <span>{item.phone_number || historyContrat.telephone || '-'}</span>
                      <span>Creation: {formatDateTime(item.created_at)}</span>
                    </div>
                    <p className="sms-history-message">{item.message_text}</p>
                    {item.error_message && (
                      <p className="sms-history-error">{item.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {renewalDialog.open && renewalDialog.contrat && (
        <div className="sms-history-overlay" onClick={closeRenewalDialog}>
          <div className="renewal-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sms-history-header">
              <div>
                <h2>Renouveler le contrat</h2>
                <p>{renewalDialog.contrat.client_name}</p>
              </div>
              <button
                type="button"
                className="sms-history-close"
                onClick={closeRenewalDialog}
                title="Fermer"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="renewal-body">
              <div className="renewal-summary">
                <div className="renewal-summary-item">
                  <span className="renewal-label">Type de contrat</span>
                  <strong>{renewalDialog.contrat.t_contrat}</strong>
                </div>
                <div className="renewal-summary-item">
                  <span className="renewal-label">Date debut suivante</span>
                  <strong>{new Date(renewalDialog.contrat.date_fin).toLocaleDateString()}</strong>
                </div>
                <div className="renewal-summary-item">
                  <span className="renewal-label">Date fin calculee</span>
                  <strong>{renewalEndDate ? new Date(renewalEndDate).toLocaleDateString() : '-'}</strong>
                </div>
              </div>

              <div className="renewal-field">
                <label htmlFor="renewal-mode-reglt">Mode de reglement</label>
                <select
                  id="renewal-mode-reglt"
                  value={renewalDialog.modeReglt}
                  onChange={(event) => setRenewalDialog((prev) => ({
                    ...prev,
                    modeReglt: event.target.value,
                  }))}
                >
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
              </div>

              <p className="renewal-note">
                La nouvelle date debut prend l ancienne date fin. La nouvelle date fin suit la meme logique que la fiche contrat selon le type et le mode de reglement choisi.
              </p>
            </div>

            <div className="renewal-actions">
              <button type="button" className="renewal-btn renewal-btn-cancel" onClick={closeRenewalDialog}>
                Annuler
              </button>
              <button
                type="button"
                className="renewal-btn renewal-btn-confirm"
                onClick={() => { void confirmRenewal(); }}
                disabled={renewingContractId === renewalDialog.contrat.id}
              >
                {renewingContractId === renewalDialog.contrat.id ? 'Renouvellement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .days-filter {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .days-filter label {
          font-size: 14px;
        }

        .days-filter select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        .stats-bar {
          background: white;
          padding: 16px 24px;
          border-radius: 8px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          display: flex;
          gap: 32px;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          align-items: baseline;
          gap: 8px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
        }

        .expired-value {
          color: #e74c3c;
        }

        .expiring-value {
          color: #f39c12;
        }

        .total-value {
          color: #3498db;
        }

        .stat-label {
          color: #666;
          font-size: 14px;
        }

        .empty-state {
          background: white;
          padding: 48px;
          border-radius: 8px;
          text-align: center;
          color: #666;
        }

        .compact-empty-state {
          padding: 24px;
          margin: 0;
          box-shadow: none;
          background: #f8fafc;
        }

        .row-critical {
          background: #f8d7da !important;
        }

        .row-warning-critical {
          background: #ffeeba !important;
        }

        .days-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
        }

        .days-badge.critical {
          background: #e74c3c;
          color: white;
        }

        .days-badge.warning-critical {
          background: #e67e22;
          color: white;
        }

        .days-badge.warning {
          background: #f39c12;
          color: white;
        }

        .days-badge.normal {
          background: #3498db;
          color: white;
        }

        .sms-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sms-icon-btn {
          border: none;
          border-radius: 10px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .sms-icon-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
        }

        .sms-icon-btn:disabled {
          cursor: not-allowed;
          opacity: 0.8;
          box-shadow: none;
        }

        .sms-send-btn {
          background: #1f9d55;
          color: white;
          padding: 8px 10px;
          gap: 8px;
          min-width: 78px;
        }

        .sms-send-btn:hover:not(:disabled) {
          background: #187943;
        }

        .sms-send-btn:disabled {
          background: #c7c7c7;
        }

        .sms-history-btn {
          width: 36px;
          height: 36px;
          background: #e8eef9;
          color: #285192;
        }

        .sms-history-btn:hover:not(:disabled) {
          background: #d5e1f6;
        }

        .contract-action-btn {
          width: 36px;
          height: 36px;
        }

        .edit-action-btn {
          background: #eef2ff;
          color: #4338ca;
        }

        .edit-action-btn:hover:not(:disabled) {
          background: #dfe5ff;
        }

        .renew-action-btn {
          background: #ecfdf3;
          color: #15803d;
        }

        .renew-action-btn:hover:not(:disabled) {
          background: #d9fbe8;
        }

        .renew-action-btn:disabled {
          background: #c7c7c7;
          color: white;
        }

        .action-icon {
          width: 18px;
          height: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .action-icon svg {
          width: 18px;
          height: 18px;
          display: block;
        }

        .sms-count-badge {
          min-width: 22px;
          padding: 2px 7px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.18);
          color: white;
          font-size: 12px;
          font-weight: 700;
          text-align: center;
        }

        .sms-history-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          z-index: 1200;
        }

        .sms-history-modal {
          width: min(760px, 100%);
          max-height: 80vh;
          background: white;
          border-radius: 18px;
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.25);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .renewal-modal {
          width: min(520px, 100%);
          background: white;
          border-radius: 18px;
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.25);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .sms-history-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .sms-history-header h2 {
          margin: 0 0 4px;
          font-size: 22px;
        }

        .sms-history-header p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
        }

        .sms-history-close {
          width: 38px;
          height: 38px;
          border: none;
          border-radius: 10px;
          background: #f1f5f9;
          color: #334155;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .sms-history-close svg {
          width: 18px;
          height: 18px;
        }

        .sms-history-list {
          padding: 20px 24px 24px;
          overflow: auto;
          display: grid;
          gap: 14px;
        }

        .renewal-body {
          padding: 20px 24px;
          display: grid;
          gap: 18px;
        }

        .renewal-summary {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        }

        .renewal-summary-item {
          padding: 14px;
          border-radius: 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          display: grid;
          gap: 6px;
        }

        .renewal-label {
          font-size: 12px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .renewal-field {
          display: grid;
          gap: 8px;
        }

        .renewal-field label {
          font-weight: 600;
          color: #0f172a;
        }

        .renewal-field select {
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #cbd5e1;
          font-size: 14px;
        }

        .renewal-note {
          margin: 0;
          color: #475569;
          line-height: 1.5;
          font-size: 14px;
        }

        .renewal-actions {
          padding: 0 24px 24px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .renewal-btn {
          border: none;
          border-radius: 10px;
          padding: 10px 16px;
          font-weight: 600;
          cursor: pointer;
        }

        .renewal-btn-cancel {
          background: #e2e8f0;
          color: #1e293b;
        }

        .renewal-btn-confirm {
          background: #15803d;
          color: white;
        }

        .renewal-btn-confirm:disabled,
        .renewal-btn-cancel:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .sms-history-item {
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 16px;
          background: #f8fafc;
        }

        .sms-history-item-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }

        .history-status-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .history-status-badge.success {
          background: #dcfce7;
          color: #166534;
        }

        .history-status-badge.failed {
          background: #fee2e2;
          color: #b91c1c;
        }

        .history-status-badge.processing {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .history-status-badge.cancelled {
          background: #e5e7eb;
          color: #4b5563;
        }

        .history-status-badge.queued {
          background: #fef3c7;
          color: #92400e;
        }

        .sms-history-date {
          color: #64748b;
          font-size: 13px;
        }

        .sms-history-meta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          color: #475569;
          font-size: 13px;
          margin-bottom: 10px;
        }

        .sms-history-message {
          margin: 0;
          color: #0f172a;
          line-height: 1.5;
          white-space: pre-wrap;
        }

        .sms-history-error {
          margin: 10px 0 0;
          color: #b91c1c;
          font-size: 13px;
        }

        @media (max-width: 768px) {
          .stats-bar {
            flex-direction: column;
            gap: 12px;
            padding: 12px 16px;
          }

          .stat-value {
            font-size: 24px;
          }

          .stat-label {
            font-size: 13px;
          }

          .days-filter {
            flex-direction: column;
            align-items: stretch;
          }

          .days-filter select {
            width: 100%;
            font-size: 16px;
          }

          .empty-state {
            padding: 24px;
          }

          .days-badge {
            font-size: 11px;
            padding: 3px 8px;
          }

          .sms-actions {
            justify-content: flex-start;
          }

          .sms-send-btn {
            min-width: 72px;
          }

          .sms-history-overlay {
            padding: 12px;
          }

          .sms-history-header,
          .sms-history-list,
          .renewal-body,
          .renewal-actions {
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default ExpiredContrats;
